import React, { useState, useEffect } from "react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { supabase } from './supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Palette,
  Sparkles,
  Dribbble,
  Search,
  User,
  Filter,
  X,
  ChevronUp,
  ExternalLink,
  Heart,
  Trash2,
  Plus,
  Loader,
  Check,
  Menu,
  CheckCircle,
  Wand2,
} from "lucide-react";

import LoginModal from './components/LoginModal';
import UsernameSetupModal from './components/UsernameSetupModal';
import CuratorAdmin from './components/CuratorAdmin';
import StitchAdmin from './components/StitchAdmin';
import PatternDesigner from './components/PatternDesigner';
import StitchLibrary from './components/StitchLibrary';
import YarnHookGuide from './components/YarnHookGuide';
import MyProjectsDashboard from './components/MyProjectsDashboard';
import LegalPage from './components/LegalPage';
import AboutPage from './components/AboutPage';
import PatternDetailModal from './components/PatternDetailModal';
import LogoMark from './components/LogoMark';

function TheSingleLoop() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isLoggedIn] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // Set to true for admin access (Ctrl+Shift+A)
  const [designerPattern, setDesignerPattern] = useState(null); // Pattern loaded in Designer
  const [editingPatternId, setEditingPatternId] = useState(null); // ID of pattern being edited (null = creating new)
  const [editSpecs, setEditSpecs] = useState(null);
  const [editingPaletteId, setEditingPaletteId] = useState(null);
  const [editStep, setEditStep] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [stitches, setStitches] = useState([]);
  const [stitchesLoading, setStitchesLoading] = useState(true);
  const [patterns, setPatterns] = useState([]);
  const [patternsLoading, setPatternsLoading] = useState(true);
  const [showStitchAdmin, setShowStitchAdmin] = useState(false);
  const [editingStitch, setEditingStitch] = useState(null);
  const [showLegalPage, setShowLegalPage] = useState(null); // null, "terms", or "privacy"
  const [showAboutPage, setShowAboutPage] = useState(false);
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUsernameSetup, setShowUsernameSetup] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [savedPatterns, setSavedPatterns] = useState([]);
  const [modalInitialTab, setModalInitialTab] = useState('details');
  const [savingPattern, setSavingPattern] = useState(null);
  const [showUserSubmit, setShowUserSubmit] = useState(false);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [showStartProjectModal, setShowStartProjectModal] = useState(false);
  const [startingProjectPattern, setStartingProjectPattern] = useState(null);
  const [startProjectNotes, setStartProjectNotes] = useState('');

  // Test Supabase connection
  useEffect(() => {
    const testSupabase = async () => {
      const { data, error } = await supabase
        .from('patterns')
        .select('*');
      
      if (error) {
        console.error('❌ Supabase error:', error);
      } else {
      }
    };
    testSupabase();
  }, []);
  
  // Resources navigation state
  const [selectedStitchId, setSelectedStitchId] = useState(null); // For deep linking from patterns

  // Admin access - Press Ctrl+Shift+A to toggle admin mode
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        setIsAdmin(!isAdmin);
        if (isAdmin) {
          // If turning admin mode OFF, close any open panels
          setShowAdminPanel(false);
          setShowStitchAdmin(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isAdmin]);

  // Load pending submissions when admin is active
  useEffect(() => {
    if (!isAdmin) return;
    const loadPending = async () => {
      const { data, error } = await supabase
        .from('patterns')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      setPendingSubmissions(data || []);
    };
    loadPending();
  }, [isAdmin]);

  // Filter states
  const [filters, setFilters] = useState({
    difficulty: [],
    category: [],
    yarnWeight: [],
    yarnMaterial: [],
    constructionMethod: [],
    hasVideo: false,
  });

  // Auth state listener
  useEffect(() => {
    // Get current session on load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkUserProfile(session.user.id);
      }
    });
  
    // Listen for auth changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUser = session?.user ?? null;
      
      // Only update if user actually changed
      if (newUser?.id !== user?.id) {
        setUser(newUser);
        if (newUser) {
          checkUserProfile(newUser.id);
        } else {
          setUserProfile(null);
          setIsAdmin(false);
          setShowUsernameSetup(false);
          setSavedPatterns([]); // Only clear when actually logging out
        }
      }
    });

  return () => subscription.unsubscribe();
}, []);

// Check if user has a profile
const checkUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error && error.code === 'PGRST116') {
    // No profile found - show setup
    setShowUsernameSetup(true);
    setUserProfile(null);
  } else if (data) {
    // Profile exists
    setUserProfile(data);
    setShowUsernameSetup(false);
    setIsAdmin(data.is_admin === true);
  }
};

  // Handle bookmark toggle
  const handleBookmarkToggle = async (patternId) => {
  if (!user) {
    setShowLoginModal(true);
    return;
  }

  setSavingPattern(patternId);
  
  // Check if there's a 'saved' status entry
  const isSaved = savedPatterns.some(sp => sp.pattern_id === patternId && sp.status === 'saved');
  
  if (isSaved) {
    // Remove bookmark (only delete 'saved' status)
    const { error } = await supabase
      .from('saved_patterns')
      .delete()
      .eq('user_id', user.id)
      .eq('pattern_id', patternId)
      .eq('status', 'saved');
    
    if (error) {
      console.error('Error removing bookmark:', error);
    } else {
      setSavedPatterns(savedPatterns.filter(sp => !(sp.pattern_id === patternId && sp.status === 'saved')));
      
      // Decrement save_count
      await supabase.rpc('decrement_save_count', { pattern_id: patternId });
    }
  } else {
    // Add bookmark
    const { data, error } = await supabase
      .from('saved_patterns')
      .insert([{
        user_id: user.id,
        pattern_id: patternId,
        status: 'saved'
      }])
      .select();
    
    if (error) {
      console.error('❌ Error saving pattern:', error);
    } else {
      if (data && data.length > 0) {
        setSavedPatterns([...savedPatterns, data[0]]);
      }
      
      // Increment save_count
      await supabase.rpc('increment_save_count', { pattern_id: patternId });
    }
  }
  
  setSavingPattern(null);
};

  // Load saved patterns when user logs in
  useEffect(() => {
    const loadSavedPatterns = async () => {
      if (!user) {
        setSavedPatterns([]);
        return;
      }

  
      const { data, error } = await supabase
        .from('saved_patterns')
        .select('*')  // ← Get all fields, not just pattern_id and status
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error loading saved patterns:', error);
      } else {
        setSavedPatterns(data || []);
      }
    };
  
    loadSavedPatterns();
  }, [user]);
  
  // Load patterns from Supabase
  useEffect(() => {
    const loadPatterns = async () => {
      try {
        const baseQuery = supabase
          .from('patterns')
          .select('*')
          .order('created_at', { ascending: false });

        const { data, error } = user
          ? await baseQuery.or(`status.eq.published,submitted_by.eq.${user.id}`)
          : await baseQuery.eq('status', 'published');
        
        if (error) {
          console.error('Error loading patterns:', error);
        } else {
          
          // Transform Supabase snake_case to camelCase for UI
          const transformedPatterns = data.map(pattern => ({
            id: pattern.id,
            title: pattern.title,
            slug: pattern.slug || null,
            patternContent: pattern.pattern_content || null,
            patternPhotos: pattern.pattern_photos || [],
            description: pattern.description,
            difficulty: pattern.difficulty,
            category: pattern.category || [],
            yarnWeight: pattern.yarn_weight,
            yarnMaterial: pattern.yarn_material,
            hook: pattern.hook || [],
            image: pattern.image,
            sourceUrl: pattern.source_url,
            siteName: pattern.site_name,
            creator: pattern.creator,
            stitches: pattern.stitches || [],
            constructionMethod: pattern.construction_method || '',
              gaugeEntries: pattern.gauge_entries || [],
              totalYards: pattern.total_yards,
              yarnColors: Array.isArray(pattern.yarn_colors) ? pattern.yarn_colors : [],
              originalSize: {
                width: pattern.original_width,
                height: pattern.original_height
              },
              videoUrl: pattern.video_url,
              videoCreator: pattern.video_creator || null,
              curatorNotes: pattern.curator_notes,
              save_count: pattern.save_count || 0,
              complete_count: pattern.complete_count || 0,
              view_count: pattern.view_count || 0,
              status: pattern.status || 'published',
              submittedBy: pattern.submitted_by || null,
              specialStitches: pattern.special_stitches || null,
            }));

          setPatterns(transformedPatterns);
        }
      } catch (err) {
        console.error('Failed to load patterns:', err);
      } finally {
        setPatternsLoading(false);
      }
    };
    
    loadPatterns();
  }, [user]);

  // Load stitches from Supabase
  useEffect(() => {
    const loadStitches = async () => {
      try {
        const { data, error } = await supabase
          .from('stitches')
          .select('*')
          .order('sort_order', { ascending: true });
        
        if (error) {
          console.error('Error loading stitches:', error);
        } else {
          
          const transformedStitches = data.map(stitch => ({
            id: stitch.id,
            name: stitch.name,
            abbreviation: stitch.abbreviation,
            description: stitch.description,
            instructions: stitch.instructions,
            image: stitch.image,
            tutorialUrl: stitch.tutorial_url,
            difficulty: stitch.difficulty,
            category: stitch.category
          }));
          
          setStitches(transformedStitches);
        }
      } catch (err) {
        console.error('Failed to load stitches:', err);
      } finally {
        setStitchesLoading(false);
      }
    };
    
    loadStitches();
  }, []);

  
  // Pattern management functions
    const handleAddPattern = async (patternData) => {
      try {
        const yarnSlots = patternData.yarnSlots; // undefined = no yarn section in form
        const totalYards = patternData.totalYards || 0;

        // Resolve yarn slots: insert any new yarns into yarn_library, use existing id for known ones
        let resolvedSlots = yarnSlots ? [...yarnSlots] : [];
        if (resolvedSlots.length > 0) {
          for (let i = 0; i < resolvedSlots.length; i++) {
            const slot = resolvedSlots[i];
            if (!slot.id) {
              const { data: newYarn, error: yarnErr } = await supabase
                .from('yarn_library')
                .insert([{
                  brand_name: slot.brand_name,
                  colorway_name: slot.colorway_name,
                  brand_url: slot.brand_url || null,
                  image_url: slot.image_url || null,
                  yards_per_skein: parseInt(slot.yards_per_skein) || null,
                  weight: slot.weight || null,
                  fiber: slot.fiber || null,
                }])
                .select()
                .single();
              if (yarnErr) throw yarnErr;
              resolvedSlots[i] = { ...slot, id: newYarn.id };
            }
          }
        }

        // Build yarn_colors: [{id, skeins, role, sort_order}] — stored directly on pattern
        const yarnColors = resolvedSlots.map((slot, idx) => {
          const totalYards = parseFloat(slot.total_yards) || 0;
          const yardsPerSkein = parseFloat(slot.yards_per_skein) || 0;
          const skeins = (totalYards && yardsPerSkein) ? Math.ceil(totalYards / yardsPerSkein) : null;
          return { id: slot.id, skeins, total_yards: totalYards || null, role: slot.role || null, sort_order: idx };
        });

        // Optional: publish yarn set as a named palette in Color Palettes section
        if (patternData.publishPalette && resolvedSlots.length > 0) {
          const { data: palette } = await supabase
            .from('color_palettes')
            .insert([{ name: patternData.title, is_published: true }])
            .select()
            .single();
          if (palette) {
            for (let idx = 0; idx < resolvedSlots.length; idx++) {
              await supabase.from('palette_yarn').insert({
                palette_id: palette.id,
                yarn_library_id: resolvedSlots[idx].id,
                sort_order: idx,
              });
            }
          }
        }

        const supabaseData = {
          title: patternData.title,
          slug: patternData.slug || null,
          pattern_content: patternData.patternContent || null,
          pattern_photos: patternData.patternPhotos?.length ? patternData.patternPhotos : null,
          description: patternData.description,
          difficulty: patternData.difficulty,
          category: patternData.category,
          yarn_weight: resolvedSlots[0]?.weight || patternData.yarnWeight || null,
          yarn_material: resolvedSlots[0]?.fiber || patternData.yarnMaterial || null,
          hook: patternData.hook,
          image: patternData.image,
          source_url: patternData.sourceUrl,
          site_name: patternData.siteName,
          creator: patternData.creator,
          stitches: patternData.stitches || [],
          construction_method: patternData.constructionMethod || null,
          gauge_entries: patternData.gaugeEntries || [],
          total_yards: totalYards,
          original_width: patternData.originalSize?.width,
          original_height: patternData.originalSize?.height,
          video_url: patternData.videoUrl,
          video_creator: patternData.videoCreator || null,
          curator_notes: patternData.curatorNotes,
          status: patternData.status || 'published',
          submitted_by: patternData.submitted_by || null,
          special_stitches: patternData.specialStitches || null,
          ...(yarnSlots !== undefined && { yarn_colors: yarnColors }),
        };

        let savedId = null;
        if (patternData.id) {
          const { error } = await supabase.from('patterns').update(supabaseData).eq('id', patternData.id);
          if (error) throw error;
          savedId = patternData.id;
        } else {
          const { data, error } = await supabase.from('patterns').insert([supabaseData]).select();
          if (error) throw error;
          savedId = data?.[0]?.id || null;
        }

        const existing = patterns.find(p => p.id === savedId);
        const updatedPattern = {
          id: savedId,
          title: patternData.title,
          slug: patternData.slug || null,
          patternContent: patternData.patternContent || null,
          patternPhotos: patternData.patternPhotos || [],
          description: patternData.description,
          difficulty: patternData.difficulty,
          category: patternData.category || [],
          yarnWeight: patternData.yarnWeight,
          yarnMaterial: patternData.yarnMaterial,
          hook: patternData.hook || [],
          image: patternData.image,
          sourceUrl: patternData.sourceUrl,
          siteName: patternData.siteName,
          creator: patternData.creator,
          stitches: patternData.stitches || [],
          constructionMethod: patternData.constructionMethod || '',
          gaugeEntries: patternData.gaugeEntries || [],
          totalYards: totalYards,
          yarnColors: yarnSlots !== undefined ? yarnColors : (existing?.yarnColors || []),
          originalSize: patternData.originalSize || { width: null, height: null },
          videoUrl: patternData.videoUrl,
          videoCreator: patternData.videoCreator || null,
          curatorNotes: patternData.curatorNotes,
          save_count: existing?.save_count || 0,
          complete_count: existing?.complete_count || 0,
          view_count: existing?.view_count || 0,
          status: patternData.status || 'published',
          submittedBy: patternData.submitted_by || null,
          specialStitches: patternData.specialStitches || null,
        };
        setPatterns(prev => [updatedPattern, ...prev.filter(p => p.id !== savedId)]);
        setSelectedPattern(prev => prev?.id === savedId ? updatedPattern : prev);
        return savedId;

      } catch (err) {
        console.error('Error saving pattern:', err);
        alert(`Failed to save pattern: ${err.message}`);
        return null;
      }
    };
  
    const handleDeletePattern = (patternId) => {
      if (window.confirm("Are you sure you want to delete this pattern?")) {
        setPatterns(patterns.filter((p) => p.id !== patternId));
      }
    };
    
    const handleEditPattern = (pattern) => {
      // Set preview data
      setEditPreview({
        title: pattern.title,
        description: pattern.description,
        image: pattern.image,
        sourceUrl: pattern.sourceUrl,
        siteName: pattern.siteName || pattern.creator,
      });
    
      // Pre-fill the admin panel with pattern data for editing
      setEditSpecs({
        difficulty: pattern.difficulty,
        category: pattern.category,
        yarnWeight: pattern.yarnWeight,
        yarnMaterial: pattern.yarnMaterial,
        hook: pattern.hook,
        stitches: pattern.stitches || [],
        constructionMethod: pattern.constructionMethod || '',
        gaugeEntries: pattern.gaugeEntries || [],
        yarnYards: pattern.totalYards?.toString() || '',
        originalWidth: pattern.originalSize?.width?.toString() || "",
        originalHeight: pattern.originalSize?.height?.toString() || "",
        videoUrl: pattern.videoUrl || "",
        videoCreator: pattern.videoCreator || "",
        curatorNotes: pattern.curatorNotes || "",
        customAttribution: "",
        customImage: pattern.image || "",
        specialStitches: pattern.specialStitches || "",
        customTitle: pattern.title || "",
        slug: pattern.slug || "",
        patternContent: pattern.patternContent || { sections: [{ title: "Instructions", steps: [""] }] },
        patternPhotos: pattern.patternPhotos || [],
      });

      // Store yarn colors for the yarn slots section
      setEditingPaletteId(pattern.yarnColors || []);

      // Store the pattern ID for updating instead of creating new
      setEditingPatternId(pattern.id);
    
      // Set step to "preview" (specs entry step) for editing
      setEditStep("preview");
    
      // Open admin panel
      setShowAdminPanel(true);
    
      };
    
    const handleStitchSaved = async () => {
      // Reload stitches from database
      try {
        const { data, error } = await supabase
          .from('stitches')
          .select('*')
          .order('sort_order', { ascending: true });
        
        if (error) throw error;
      
        const transformedStitches = data.map(stitch => ({
          id: stitch.id,
          name: stitch.name,
          abbreviation: stitch.abbreviation,
          description: stitch.description,
          instructions: stitch.instructions,
          image: stitch.image,
          tutorialUrl: stitch.tutorial_url,
          difficulty: stitch.difficulty,
          category: stitch.category
        }));
        
        setStitches(transformedStitches);
      } catch (err) {
        console.error('Failed to reload stitches:', err);
      }
    };
    
    const handleDeleteStitch = async (stitchId) => {
      if (window.confirm("Are you sure you want to delete this stitch?")) {
        try {
          const { error } = await supabase
            .from('stitches')
            .delete()
            .eq('id', stitchId);
          
          if (error) throw error;
          
          alert("Stitch deleted!");
          handleStitchSaved();
        } catch (err) {
          console.error('Error deleting stitch:', err);
          alert(`Failed to delete: ${err.message}`);
        }
      }
    };
    
    const handleEditStitch = (stitch) => {
      setEditingStitch(stitch);
      setShowStitchAdmin(true);
    };
  const projects = [
    {
      name: "Sage Green Market Bag",
      pattern: "Modern Market Bag",
      progress: 65,
      yarn: "Lion Brand 24/7 Cotton - Sage",
      hook: "H/8 (5.0mm)",
      started: "Jan 15, 2026",
      notes: "Using a slightly smaller hook for tighter stitches",
    },
    {
      name: "Baby Blanket for Emma",
      pattern: "Granny Square Blanket",
      progress: 40,
      yarn: "Red Heart Soft - Cloud",
      hook: "I/9 (5.5mm)",
      started: "Jan 8, 2026",
      notes: "Making 48 squares total, 19 completed so far",
    },
    {
      name: "Cozy Cardigan",
      pattern: "Chunky Cardigan",
      progress: 25,
      yarn: "Big Twist Soft - Oatmeal",
      hook: "K/10.5 (6.5mm)",
      started: "Dec 28, 2025",
      notes: "Working on the back panel, need to order more yarn",
    },
  ];

  // Filter options
  const filterOptions = {
    difficulty: ["Beginner Friendly", "Beginner", "Intermediate", "Advanced"],
    category: [
      "Clothing",
      "Accessories",
      "Home Decor",
      "Blankets",
      "Kids",
      "Holiday",
      "Pet",
    ],
    yarnWeight: [
      "0 Lace",
      "1 Superfine",
      "2 Fine",
      "3 Light",
      "4 Medium/Worsted",
      "5 Bulky",
      "6 Super Bulky",
      "7 Jumbo",
    ],
    constructionMethod: [
      "Rows",
      "In the Round",
      "Corner to Corner (C2C)",
      "Granny Square",
      "Tapestry / Mosaic",
      "Other",
    ],
    yarnMaterial: [
      "Acrylic",
      "Cotton",
      "Wool",
      "Bamboo",
      "Chenille",
      "Nylon",
      "Recycled",
      "Blend",
    ],
  };

  // Filter logic
  const toggleFilter = (category, value) => {
    setFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((v) => v !== value)
        : [...prev[category], value],
    }));
  };

  const clearFilters = () => {
    setFilters({
      difficulty: [],
      category: [],
      yarnWeight: [],
      yarnMaterial: [],
      constructionMethod: [],
      hasVideo: false,
    });
  };

  const hasActiveFilters = Object.values(filters).some((v) => Array.isArray(v) ? v.length > 0 : v);

  // Apply filters
    const filteredPatterns = patterns.filter((pattern) => {
      // Apply category filters
      if (
        filters.difficulty.length &&
        !filters.difficulty.includes(pattern.difficulty)
      )
        return false;
      if (
        filters.category.length &&
        !pattern.category.some((cat) => filters.category.includes(cat))
      )
        return false;
      if (
        filters.yarnWeight.length &&
        !filters.yarnWeight.includes(pattern.yarnWeight)
      )
        return false;
      if (
        filters.yarnMaterial.length &&
        !filters.yarnMaterial.includes(pattern.yarnMaterial)
      )
        return false;
      if (filters.constructionMethod.length && !filters.constructionMethod.includes(pattern.constructionMethod)) return false;
      if (filters.hasVideo && !pattern.videoUrl) return false;

      // Apply search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          pattern.title.toLowerCase().includes(query) ||
          (pattern.description && pattern.description.toLowerCase().includes(query)) ||
          (pattern.siteName && pattern.siteName.toLowerCase().includes(query)) ||
          (pattern.creator && pattern.creator.toLowerCase().includes(query)) ||
          pattern.category.some(cat => cat.toLowerCase().includes(query))
        );
      }
      
      return true;
    });

  // Track view when pattern detail opens
    useEffect(() => {
    if (selectedPattern) {
      // Increment view count
      const incrementView = async () => {
        const { error } = await supabase
          .from('patterns')
          .update({ view_count: selectedPattern.view_count + 1 })
          .eq('id', selectedPattern.id);
        
        if (error) {
          console.error('Error tracking view:', error);
        }
      };
      incrementView();
    }
  }, [selectedPattern]);

  // Action handlers with tracking
 const handleSavePattern = (pattern) => {
  handleBookmarkToggle(pattern.id);
};

const handleTrackProject = async (pattern) => {
  if (!user) {
    alert('Please sign in to start a project.');
    return;
  }

  const hasInProgress = savedPatterns.some(sp => sp.pattern_id === pattern.id && sp.status === 'in_progress');
  
  if (hasInProgress) {
    alert('This project is already in progress!');
    navigate('/projects');
    return;
  }
  
  // Open start modal WITHOUT closing pattern detail
  setStartingProjectPattern(pattern);
  setShowStartProjectModal(true);
  // DON'T call setSelectedPattern(null) here
};

  const handleCustomize = (pattern) => {
    // In production: updatePatternStats(pattern.id, 'customizes')
    setDesignerPattern(pattern);
    setSelectedPattern(null);
    navigate('/designer');
  };

  const handleCancelStart = () => {
    setShowStartProjectModal(false);
    setStartingProjectPattern(null);
    setStartProjectNotes('');
  };

  const handleConfirmStartProject = async () => {
    const { error } = await supabase
      .from('saved_patterns')
      .insert({
        user_id: user.id,
        pattern_id: startingProjectPattern.id,
        status: 'in_progress',
        started_at: new Date().toISOString(),
        notes: startProjectNotes || null
      });

    if (error) {
      console.error('Error starting project:', error);
      alert('Failed to start project. Please try again.');
    } else {
      setShowStartProjectModal(false);
      setStartingProjectPattern(null);
      setStartProjectNotes('');
      setSelectedPattern(null);
      navigate('/projects');

      const { data } = await supabase
        .from('saved_patterns')
        .select('*')
        .eq('user_id', user.id);
      setSavedPatterns(data || []);

      alert('Project started! See it in the In Progress tab.');
    }
  };

  const handleNavigateToStitch = (stitchId) => {
    setSelectedStitchId(stitchId);
    setSelectedPattern(null);
    navigate('/stitches');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToView = (view) => {
    const pathMap = { patterns: '/', designer: '/designer', stitches: '/stitches', yarn: '/yarn', myprojects: '/projects' };
    navigate(pathMap[view] || '/');
  };

  const handlePatternClick = (pattern) => {
    // In production: updatePatternStats(pattern.id, 'patternClicks')
    // Link opens in new tab automatically
  };

  const colors = {
    sageGreen: "#a3b18a",
    dustyRust: "#c1795b",
    mustard: "#d4a574",
    bgCream: "#faf8f5",
    borderGreige: "#e8e0d5",
    charcoal: "#2a2a2a",
    darkGray: "#6b6b6b",
    brownGray: "#8b7355",
  };

  const fonts = {
    display: "'Pacifico', cursive",
    body: "'Karla', sans-serif",
  };

  // Dynamic page title
  useEffect(() => {
    const titles = {
      '/': 'The Single Loop — Crochet Pattern Curation',
      '/designer': 'Pattern Designer — The Single Loop',
      '/projects': 'My Projects — The Single Loop',
      '/stitches': 'Stitch Library — The Single Loop',
      '/yarn': 'Yarn — The Single Loop',
    };
    document.title = titles[location.pathname] || 'The Single Loop';
  }, [location.pathname]);

  // Open modal when navigating directly to /patterns/:slug
  useEffect(() => {
    if (location.pathname.startsWith('/patterns/') && patterns.length > 0 && !selectedPattern) {
      const slug = location.pathname.replace('/patterns/', '');
      const pattern = patterns.find(p => p.slug === slug);
      if (pattern) setSelectedPattern(pattern);
    }
  }, [location.pathname, patterns]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #faf8f5 0%, #f5ede8 30%, #f0f0e8 60%, #eff2ed 100%)",
        fontFamily: fonts.body,
      }}
    >
      {/* Header */}
     <header className="site-header">
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Mobile Menu Button - ONLY shows on mobile */}
          <button
            className="mobile-menu-btn"
            style={{
              display: "none", // Hidden on desktop
              background: "rgba(255,255,255,0.2)",
              border: "none",
              padding: "0.75rem",
              borderRadius: "12px",
              cursor: "pointer",
              color: "#fff"
            }}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            <Menu size={24} />
          </button>
      
          <h1
            style={{
              fontSize: "1.8rem",
              fontFamily: fonts.display,
              color: "#fff",
              margin: 0,
              textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            The Single Loop
          </h1>
      
          <div
            style={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
            }}
          >
            {/* Desktop Search - Hidden on mobile */}
            <div className="desktop-search" style={{ position: "relative", width: "300px" }}>
              <input
                type="text"
                placeholder="Search patterns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.65rem 1rem 0.65rem 2.5rem",
                  borderRadius: "50px",
                  border: "2px solid rgba(255,255,255,0.3)",
                  background: "rgba(255,255,255,0.95)",
                  fontSize: "0.9rem",
                  outline: "none",
                }}
              />
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#8b7355",
                }}
              />
            </div>
      
            {/* Mobile Search Button - ONLY shows on mobile */}
            <button
              className="mobile-search-btn"
              onClick={() => setShowMobileSearch(!showMobileSearch)}
              style={{
                display: "none", // Hidden on desktop
                background: "rgba(255,255,255,0.2)",
                border: "none",
                padding: "0.75rem",
                borderRadius: "12px",
                cursor: "pointer",
                color: "#fff"
              }}
            >
              <Search size={24} />
            </button>
      
            {/* My Projects Button - Only show when logged in */}
          {user && (
            <button
              onClick={() => navigate('/projects')}
              style={{
                background: "#fff",
                border: "none",
                padding: "0.65rem 1.5rem",
                borderRadius: "50px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.9rem",
                fontWeight: "600",
                color: "#8b7355",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <User size={16} />
              <span className="desktop-only">My Projects</span>
            </button>
          )}

              {/* Auth Button */}
              {user ? (
                <button
                  onClick={async () => {
                    const { error } = await supabase.auth.signOut();
                    if (error) {
                      console.error('Sign out error:', error);
                    } else {
                      // Force reload to clear state
                      window.location.reload();
                    }
                  }}
                  style={{
                    padding: "0.65rem 1.5rem",
                    background: "#fff",
                    border: "none",
                    borderRadius: "50px",
                    color: "#8b7355",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontFamily: fonts.body,
                    fontWeight: "600",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}
              >
                Sign Out
              </button>
              ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                style={{
                  padding: "0.65rem 1.5rem",
                  background: colors.sageGreen,
                  border: "none",
                  borderRadius: "50px",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontFamily: fonts.body,
                  fontWeight: "600",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>
          
    {/* Mobile Search Dropdown */}
    {showMobileSearch && (
      <div
        style={{
          background: "#fff",
          padding: "1rem",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          position: "sticky",
          top: "60px",
          zIndex: 98
        }}
      >
        <div style={{ position: "relative", maxWidth: "600px", margin: "0 auto" }}>
          <input
            type="text"
            placeholder="Search patterns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            style={{
              width: "100%",
              padding: "0.75rem 1rem 0.75rem 2.5rem",
              borderRadius: "50px",
              border: "2px solid #e8e0d5",
              fontSize: "1rem",
              outline: "none"
            }}
          />
          <Search
            size={18}
            style={{
              position: "absolute",
              left: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#8b7355"
            }}
          />
        </div>
      </div>
    )}
    {/* Mobile Menu Overlay */}
    {showMobileMenu && (
      <div
        onClick={() => setShowMobileMenu(false)}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 999
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "280px",
            height: "100vh",
            background: "#fff",
            boxShadow: "4px 0 20px rgba(0,0,0,0.15)",
            padding: "2rem 1.5rem",
            overflowY: "auto"
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setShowMobileMenu(false)}
            style={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              background: "none",
              border: "none",
              fontSize: "2rem",
              color: colors.brownGray,
              cursor: "pointer",
              padding: "0.5rem"
            }}
          >
            ×
          </button>
    
          {/* Menu Title */}
          <h2
            style={{
              fontSize: "1.5rem",
              fontFamily: fonts.display,
              color: colors.charcoal,
              marginBottom: "2rem"
            }}
          >
            Menu
          </h2>
    
          {/* Menu Items */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {[
              { id: "patterns", path: "/", label: "Patterns", icon: Home },
              { id: "designer", path: "/designer", label: "Designer", icon: Palette },
              { id: "stitches", path: "/stitches", label: "Stitches", icon: Sparkles },
              { id: "yarn", path: "/yarn", label: "Yarn", icon: Dribbble },
              { id: "myprojects", path: "/projects", label: "My Projects", icon: User }
            ].map((item) => {
              const Icon = item.icon;
              const isActive = item.path === '/' ? location.pathname === '/' : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.path);
                    setShowMobileMenu(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "1rem",
                    background: isActive ? colors.bgCream : "transparent",
                    border: isActive ? `2px solid ${colors.sageGreen}` : "2px solid transparent",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: isActive ? colors.sageGreen : colors.brownGray,
                    textAlign: "left",
                    width: "100%",
                    transition: "all 0.2s"
                  }}
                >
                  <Icon size={20} />
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Divider + secondary links */}
          <div style={{ borderTop: `1px solid ${colors.bgCream}`, marginTop: "1.5rem", paddingTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {[
              { label: "About", onClick: () => { setShowAboutPage(true); setShowMobileMenu(false); } },
              { label: "Terms & Privacy", onClick: () => { setShowLegalPage("terms"); setShowMobileMenu(false); } }
            ].map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                style={{
                  display: "flex", alignItems: "center", padding: "0.75rem 1rem",
                  background: "transparent", border: "none", borderRadius: "12px",
                  cursor: "pointer", fontSize: "0.9rem", fontWeight: "500",
                  color: colors.brownGray, textAlign: "left", width: "100%"
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    )}

      {/* Navigation */}
      <nav className="site-nav">
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            display: "flex",
            gap: "0.5rem",
            padding: "0 2rem",
          }}
        >
          {[
            { id: "patterns", path: "/", label: "Patterns", icon: Home },
            { id: "designer", path: "/designer", label: "Designer", icon: Palette },
            { id: "stitches", path: "/stitches", label: "Stitches", icon: Sparkles },
            { id: "yarn", path: "/yarn", label: "Yarn", icon: Dribbble },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.path === '/' ? location.pathname === '/' : location.pathname === tab.path || location.pathname.startsWith(tab.path + '/');
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                style={{
                  background:
                    isActive
                      ? "linear-gradient(135deg, #d4a574 0%, #c89b68 100%)"
                      : "transparent",
                  color: isActive ? "#fff" : "#8b7355",
                  border: "none",
                  padding: "1rem 1.5rem",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  borderRadius: "8px 8px 0 0",
                  transition: "all 0.3s",
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Pattern Detail Modal */}
      {selectedPattern && (
        <PatternDetailModal
          selectedPattern={selectedPattern}
          initialTab={modalInitialTab}
          savedPatterns={savedPatterns}
          stitches={stitches}
          stitchesLoading={stitchesLoading}
          isAdmin={isAdmin}
          colors={colors}
          fonts={fonts}
          onClose={() => { setSelectedPattern(null); setModalInitialTab('details'); navigate('/'); }}
          onSave={handleSavePattern}
          onTrackProject={handleTrackProject}
          onCustomize={handleCustomize}
          onNavigateToStitch={handleNavigateToStitch}
          showStartProjectModal={showStartProjectModal}
          startingProjectPattern={startingProjectPattern}
          startProjectNotes={startProjectNotes}
          setStartProjectNotes={setStartProjectNotes}
          onCancelStart={handleCancelStart}
          onConfirmStart={handleConfirmStartProject}
        />
      )}

    {/* Admin Panel (Ctrl+Shift+A to open) */}
      {showAdminPanel && stitches && stitches.length > 0 && (
        <CuratorAdmin
            onClose={() => {
              setShowAdminPanel(false);
              setEditingPatternId(null);
              setEditSpecs(null);
              setEditingPaletteId(null);
              setEditStep(null);
              setEditPreview(null);
            }}
            onAddPattern={async (patternData) => {
              const savedId = await handleAddPattern(patternData);
              if (savedId) {
                alert(patternData.id ? "Pattern updated!" : "Pattern added!");
              }
            }}
            editingPatternId={editingPatternId}
            initialSpecs={editSpecs}
            initialYarnColors={editingPaletteId}
            initialStep={editStep}
            initialPreview={editPreview}
            onResetEdit={() => {
              setEditingPatternId(null);
              setEditSpecs(null);
              setEditingPaletteId(null);
              setEditStep(null);
              setEditPreview(null);
            }}
            stitches={stitches}
            onStitchSaved={handleStitchSaved}
            pendingSubmissions={pendingSubmissions}
            onPendingUpdate={setPendingSubmissions}
          />
        )}

    {/* Stitch Admin Panel */}
    {showStitchAdmin && (
      <StitchAdmin
        onClose={() => {
          setShowStitchAdmin(false);
          setEditingStitch(null);
        }}
        onSave={handleStitchSaved}
        editingStitch={editingStitch}
      />
    )}

    {/* User Submit a Pattern overlay */}
    {showUserSubmit && stitches && stitches.length > 0 && (
      <CuratorAdmin
        userMode={true}
        currentUser={user}
        onClose={() => setShowUserSubmit(false)}
        onAddPattern={async (patternData) => {
          const savedId = await handleAddPattern(patternData);
          setShowUserSubmit(false);
          if (patternData.status === 'personal' && savedId) {
            const alsoShare = window.confirm(
              "Pattern saved to your library!\n\nWould you also like to submit it for community sharing?\n\n• Your pattern will be reviewed before sharing\n• Our team may make minor edits\n• Submissions that don't meet guidelines may not be published"
            );
            if (alsoShare) {
              await supabase.from('patterns').update({ status: 'pending' }).eq('id', savedId);
              await handleAddPattern({ ...patternData, id: savedId, status: 'pending' });
              alert("Submitted for review! Check your My Patterns tab in your dashboard.");
            }
          } else if (patternData.status === 'pending') {
            alert("Submitted for review! It's saved in your My Patterns tab as 'In Review'.");
          }
        }}
        stitches={stitches}
        onStitchSaved={handleStitchSaved}
      />
    )}

    {/* Legal Pages */}
    {showLegalPage && (
      <LegalPage
        type={showLegalPage}
        onClose={() => setShowLegalPage(null)}
        colors={colors}
        fonts={fonts}
      />
    )}

    {/* About Page */}
    {showAboutPage && (
      <AboutPage
        onClose={() => setShowAboutPage(false)}
        colors={colors}
        fonts={fonts}
      />
    )}

      {/* Main Content */}
      <main className="site-main">
        {/* Patterns View */}
        {location.pathname === '/' && (
          <>
            {patternsLoading ? (
              <div style={{ textAlign: "center", padding: "4rem" }}>
                <Loader size={48} style={{ animation: "spin 1s linear infinite" }} />
                <p style={{ marginTop: "1rem", color: colors.brownGray }}>
                  Loading patterns...
                </p>
              </div>
            ) : (
              <div>
              

            {/* Filters Header */}
            {(() => {
              const isMobile = window.innerWidth < 768;
              return (
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                gap: isMobile ? "1rem" : "0",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: isMobile ? "1.5rem" : "2rem",
                    color: user && userProfile?.username ? colors.dustyRust : colors.charcoal,
                    marginBottom: "0.5rem",
                    fontFamily: fonts.display,
                  }}
                >
                  {user && userProfile?.username ? `Welcome back, ${userProfile.username}` : "Discover Free Crochet Patterns"}
                </h2>
                <p style={{ color: colors.darkGray, fontSize: "0.95rem", margin: "0.5rem 0 0 0" }}>
                  {filteredPatterns.length} pattern{filteredPatterns.length !== 1 ? "s" : ""} found
                </p>
              </div>

              <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                {/* Submit a Pattern - Logged-in users */}
                {user && !isAdmin && (
                  <button
                    onClick={() => setShowUserSubmit(true)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.75rem 1.5rem",
                      background: `linear-gradient(135deg, ${colors.dustyRust} 0%, #a8674f 100%)`,
                      color: "#fff",
                      border: "none",
                      borderRadius: "50px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      boxShadow: "0 4px 12px rgba(193, 121, 91, 0.3)"
                    }}
                  >
                    <Plus size={18} />
                    Submit a Pattern
                  </button>
                )}

                {/* Add Pattern Button - Admin Only */}
                {isAdmin && (
                  <button
                    onClick={() => setShowAdminPanel(true)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.75rem 1.5rem",
                      background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                      color: "#fff",
                      border: "none",
                      borderRadius: "50px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      boxShadow: "0 4px 12px rgba(163, 177, 138, 0.3)"
                    }}
                  >
                    <Plus size={18} />
                    Add Pattern
                  </button>
                )}
            
                {/* Filters Button */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.75rem 1.5rem",
                    background: showFilters ? colors.mustard : "#fff",
                    color: showFilters ? "#fff" : colors.brownGray,
                    border: `2px solid ${
                      showFilters ? colors.mustard : colors.borderGreige
                    }`,
                    borderRadius: "50px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                  }}
                >
                  <Filter size={16} />
                  Filters
                  {hasActiveFilters && (
                    <span
                      style={{
                        background: colors.dustyRust,
                        color: "#fff",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: "700",
                      }}
                    >
                      {Object.values(filters).flat().length}
                    </span>
                  )}
                </button>
              </div>
            </div>
              ); })()}

         {/* Filter Panel */}
            {showFilters && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: "16px",
                  padding: "2rem",
                  marginBottom: "2rem",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1.5rem",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      color: colors.charcoal,
                      fontWeight: "700",
                      margin: 0,
                    }}
                  >
                    Filter Patterns
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: colors.dustyRust,
                          fontSize: "0.9rem",
                          fontWeight: "600",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <X size={14} /> Clear All
                      </button>
                    )}
                    <button
                      onClick={() => setShowFilters(false)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: colors.brownGray,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        padding: "0.25rem",
                      }}
                      title="Close filters"
                    >
                      <ChevronUp size={20} />
                    </button>
                  </div>
                </div>

                {/* Filter Options */}
                {Object.entries(filterOptions).map(([category, options]) => (
                  <div key={category} style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        fontSize: "0.9rem",
                        color: colors.brownGray,
                        fontWeight: "700",
                        marginBottom: "0.75rem",
                        textTransform: "capitalize",
                      }}
                    >
                      {category === "yarnWeight"
                        ? "Yarn Weight"
                        : category === "yarnMaterial"
                        ? "Yarn Material"
                        : category}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                      }}
                    >
                      {options.map((option) => {
                        const isActive = filters[category].includes(option);
                        return (
                          <button
                            key={option}
                            onClick={() => toggleFilter(category, option)}
                            style={{
                              padding: "0.5rem 1rem",
                              background: isActive ? colors.sageGreen : "#fff",
                              color: isActive ? "#fff" : colors.brownGray,
                              border: `2px solid ${
                                isActive
                                  ? colors.sageGreen
                                  : colors.borderGreige
                              }`,
                              borderRadius: "50px",
                              fontSize: "0.85rem",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Has Video Toggle */}
                <div style={{ marginBottom: "0.5rem" }}>
                  <h4 style={{ fontSize: "0.9rem", color: colors.brownGray, fontWeight: "700", marginBottom: "0.75rem" }}>
                    Video Tutorial
                  </h4>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, hasVideo: !prev.hasVideo }))}
                    style={{
                      padding: "0.5rem 1rem",
                      background: filters.hasVideo ? colors.sageGreen : "#fff",
                      color: filters.hasVideo ? "#fff" : colors.brownGray,
                      border: `2px solid ${filters.hasVideo ? colors.sageGreen : colors.borderGreige}`,
                      borderRadius: "50px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    🎬 Has Video Tutorial
                  </button>
                </div>
              </div>
            )}

            {/* Pattern Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "2rem",
              }}
            >
              {filteredPatterns.map((pattern) => (
                <div
                  key={pattern.id}
                  style={{
                    background: "#fff",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                    transition: "all 0.3s",
                    position: "relative",
                  }}
                >
                  {/* Admin Buttons */}
                  {isAdmin && (
                    <>
                      {/* Edit Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPattern(pattern);
                        }}
                        style={{
                          position: "absolute",
                          top: "1rem",
                          left: "3.5rem",
                          background: "rgba(255,255,255,0.95)",
                          border: "none",
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: colors.sageGreen,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          zIndex: 10,
                          fontWeight: "bold",
                        }}
                        title="Edit pattern (Admin only)"
                      >
                        ✏️
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePattern(pattern.id);
                        }}
                        style={{
                          position: "absolute",
                          top: "1rem",
                          left: "1rem",
                          background: "rgba(255,255,255,0.95)",
                          border: "none",
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          color: colors.dustyRust,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          zIndex: 10,
                        }}
                        title="Delete pattern (Admin only)"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}

             {/* Bookmark Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSavePattern(pattern);
                }}
                style={{
                  position: "absolute",
                  top: "1rem",
                  right: "1rem",
                  background: savedPatterns.some(sp => sp.pattern_id === pattern.id && sp.status === 'saved')
                    ? colors.dustyRust
                    : "rgba(255,255,255,0.95)",
                  border: `2px solid ${colors.dustyRust}`,
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: savedPatterns.some(sp => sp.pattern_id === pattern.id && sp.status === 'saved')
                    ? "#fff"
                    : colors.dustyRust,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 10,
                  transition: "all 0.3s",
                }}
                title={savedPatterns.some(sp => sp.pattern_id === pattern.id && sp.status === 'saved') ? "Saved!" : "Save pattern"}
              >
                {(() => {
                  const isSaved = savedPatterns.some(sp => sp.pattern_id === pattern.id && sp.status === 'saved');
                  return <Heart size={16} fill={isSaved ? "currentColor" : "none"} />;
                })()}
              </button>

                  <div
                    onClick={() => { setSelectedPattern(pattern); if (pattern.slug) navigate(`/patterns/${pattern.slug}`, { replace: true }); }}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      style={{
                        position: "relative",
                        paddingTop: "133%",
                        background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {pattern.image ? (
                        <img
                          src={pattern.image}
                          alt={pattern.title}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div style={{ opacity: 0.3, position: "absolute" }}>
                          <LogoMark width={140} height={56} />
                        </div>
                      )}
                      {pattern.status !== 'published' && pattern.submittedBy === user?.id && (
                        <div style={{
                          position: "absolute",
                          top: "0.75rem",
                          left: "0.75rem",
                          background: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          padding: "0.25rem 0.6rem",
                          borderRadius: "50px",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          zIndex: 5,
                        }}>
                          {pattern.status === 'pending' ? 'In Review' : 'Private'}
                        </div>
                      )}
                      {user && (() => {
                        const projectStatus = savedPatterns.find(sp => sp.pattern_id === pattern.id && (sp.status === 'completed' || sp.status === 'in_progress'))?.status;
                        return null;
                      })()}
                      <div
                        style={{
                          position: "absolute",
                          bottom: "1rem",
                          left: "1rem",
                          background: "rgba(255,255,255,0.95)",
                          padding: "0.35rem 0.75rem",
                          borderRadius: "50px",
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          color:
                            pattern.difficulty === "Beginner Friendly"
                              ? "#9ec5d4"
                              : pattern.difficulty === "Beginner"
                              ? colors.sageGreen
                              : pattern.difficulty === "Intermediate"
                              ? colors.mustard
                              : colors.dustyRust,
                        }}
                      >
                        {pattern.difficulty}
                      </div>
                    </div>
                    <div style={{ padding: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                        <h3 style={{ fontSize: "1.1rem", fontWeight: "700", margin: 0, color: colors.charcoal }}>
                          {pattern.title}
                        </h3>
                        {user && (() => {
                          const projectStatus = savedPatterns.find(sp => sp.pattern_id === pattern.id && (sp.status === 'completed' || sp.status === 'in_progress'))?.status;
                          if (!projectStatus) return null;
                          const isCompleted = projectStatus === 'completed';
                          return (
                            <div style={{ background: isCompleted ? "#93A87A" : "#DDAC23", borderRadius: "50%", width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {isCompleted ? <CheckCircle size={14} color="#fff" /> : <Wand2 size={12} color="#fff" />}
                            </div>
                          );
                        })()}
                      </div>
                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: colors.darkGray,
                          marginBottom: pattern.specialStitches ? "0.5rem" : "1rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>From: {pattern.siteName}</span>
                        {pattern.sourceUrl ? (
                          <a
                            href={pattern.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.2rem",
                              fontSize: "0.78rem",
                              fontWeight: "600",
                              color: colors.dustyRust,
                              textDecoration: "underline",
                            }}
                          >
                            View Pattern <ExternalLink size={11} />
                          </a>
                        ) : pattern.patternContent?.sections?.some(s => s.steps?.some(st => st.trim())) ? (
                          <button
                            onClick={e => { e.stopPropagation(); setModalInitialTab('pattern'); setSelectedPattern(pattern); if (pattern.slug) navigate(`/patterns/${pattern.slug}`); }}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.2rem",
                              fontSize: "0.78rem", fontWeight: "600", color: colors.dustyRust,
                              textDecoration: "underline", background: "none", border: "none",
                              cursor: "pointer", padding: 0,
                            }}
                          >
                            View Pattern
                          </button>
                        ) : null}
                      </div>

                      {/* Special Stitches placeholder pills */}
                      {pattern.specialStitches && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: "1rem" }}>
                          {pattern.specialStitches.split(',').map(s => s.trim()).filter(Boolean).map((name, i) => (
                            <span key={i} style={{
                              fontSize: "0.75rem",
                              padding: "0.2rem 0.55rem",
                              border: `1.5px dashed ${colors.brownGray}`,
                              borderRadius: "50px",
                              color: colors.brownGray,
                              fontWeight: "600",
                            }}>
                              {name}
                            </span>
                          ))}
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredPatterns.length === 0 && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: "16px",
                  padding: "4rem 2rem",
                  textAlign: "center",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                }}
              >
                <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔍</div>
                <h3
                  style={{
                    fontSize: "1.5rem",
                    color: colors.charcoal,
                    marginBottom: "0.5rem",
                  }}
                >
                  No patterns found
                </h3>
                <p style={{ color: colors.darkGray, marginBottom: "1.5rem" }}>
                  Try adjusting your filters to see more results
                </p>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: "0.75rem 1.5rem",
                    background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                    color: "#fff",
                    border: "none",
                    borderRadius: "50px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}
      </>
    )}

    {/* My Projects View */}
    {location.pathname === '/projects' && (
      <MyProjectsDashboard
        savedPatterns={savedPatterns}
        patterns={patterns}
        setSelectedPattern={setSelectedPattern}
        colors={colors}
        fonts={fonts}
        user={user}
        supabase={supabase}
        setActiveView={navigateToView}
        setSavedPatterns={setSavedPatterns}
        onAddPattern={handleAddPattern}
        stitches={stitches}
      />
    )}

       {/* Designer View */}
        {location.pathname === '/designer' && (
          <PatternDesigner
            pattern={designerPattern}
            colors={colors}
            fonts={fonts}
            patterns={patterns}
            user={user}
            supabase={supabase}
          />
        )}

      {/* Stitches View */}
        {location.pathname === '/stitches' && (
          <StitchLibrary
            colors={colors}
            fonts={fonts}
            initialStitchId={selectedStitchId}
            stitches={stitches}
            isAdmin={isAdmin}
            onAddStitch={() => setShowStitchAdmin(true)}
            onEditStitch={handleEditStitch}
            onDeleteStitch={handleDeleteStitch}
            patterns={patterns}
            onPatternClick={(pattern) => {
              navigate('/');
              setSelectedPattern(pattern);
            }}
          />
        )}

      {/* Yarn View */}
        {location.pathname === '/yarn' && (
          <YarnHookGuide
            colors={colors}
            fonts={fonts}
            isAdmin={isAdmin}
          />
        )}
      </main>

      {/* Footer */}
      <footer
        style={{
          background: colors.charcoal,
          color: "#fff",
          padding: "2rem",
          marginTop: "4rem"
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto"
          }}
        >
          {/* Footer Links */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "2rem",
              flexWrap: "wrap",
              marginBottom: "1.5rem"
            }}
          >
            <h3
              style={{
                fontSize: "1.5rem",
                fontFamily: fonts.display,
                margin: 0,
                color: colors.mustard
              }}
            >
              The Single Loop
            </h3>
            
            <span style={{ color: colors.borderGreige }}>|</span>
            
            <a
              href="mailto:andrea@thesingleloop.com"
              style={{
                color: "#fff",
                textDecoration: "none",
                fontSize: "0.95rem",
                opacity: 0.8
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = 1 }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.8 }}
            >
              Contact
            </a>

            <span style={{ color: colors.borderGreige }}>|</span>
            
            <button
              onClick={() => setShowAboutPage(true)}
              style={{
                background: "none",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: "0.95rem",
                opacity: 0.8,
                padding: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = 1 }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.8 }}
            >
              About
            </button>
                
            <span style={{ color: colors.borderGreige }}>|</span>
            
            <button
              onClick={() => setShowLegalPage("terms")}
              style={{
                background: "none",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: "0.95rem",
                opacity: 0.8,
                padding: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = 1 }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.8 }}
            >
              Terms & Conditions
            </button>
            
            <span style={{ color: colors.borderGreige }}>|</span>
            
            <button
              onClick={() => setShowLegalPage("privacy")}
              style={{
                background: "none",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: "0.95rem",
                opacity: 0.8,
                padding: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = 1 }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.8 }}
            >
              Privacy Policy
            </button>
          </div>
      
          {/* Copyright */}
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.2)",
              paddingTop: "1.5rem",
              textAlign: "center",
              fontSize: "0.9rem",
              opacity: 0.7
            }}
          >
            © {new Date().getFullYear()} The Single Loop. All rights reserved.
            <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", opacity: 0.8 }}>
              By using this site, you agree to our{" "}
              <button onClick={() => setShowLegalPage("terms")} style={{ background: "none", border: "none", color: "#fff", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem", padding: 0 }}>Terms</button>
              {" "}and{" "}
              <button onClick={() => setShowLegalPage("privacy")} style={{ background: "none", border: "none", color: "#fff", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem", padding: 0 }}>Privacy Policy</button>.
            </div>
          </div>
        </div>
      </footer>

     {/* Login Modal */}
     {showLoginModal && (
      <LoginModal
       onClose={() => setShowLoginModal(false)}
       colors={colors}
       fonts={fonts}
      />
     )}
        
      {/* Username Setup Modal */}
      {showUsernameSetup && user && (
       <UsernameSetupModal
        user={user}
         onComplete={() => {
          setShowUsernameSetup(false);
          checkUserProfile(user.id);
          }}
        colors={colors}
        fonts={fonts}
        />
       )}
  
     <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Pacifico&family=Karla:wght@400;600;700&display=swap');
      
      * { 
        box-sizing: border-box; 
      }
      
      body { 
        margin: 0; 
        padding: 0; 
      }
      
      /* Base Styles */
      .site-header {
        background: linear-gradient(135deg, #a3b18a 0%, #8e9775 100%);
        padding: 1.5rem 2rem;
        box-shadow: 0 4px 20px rgba(163, 177, 138, 0.25);
        position: sticky;
        top: 0;
        z-index: 100;
        border-bottom: 3px solid #7d8a68;
      }
      
      .site-nav {
        background: #fff;
        border-bottom: 2px solid #e8e0d5;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        position: sticky;
        top: 68px;
        z-index: 99;
      }
      
      .site-main {
        max-width: 1400px;
        margin: 0 auto;
        padding: 2rem;
      }
      
      /* Hide mobile buttons on desktop */
      .mobile-menu-btn,
      .mobile-search-btn {
        display: none !important;
      }
      
    /* Mobile Responsive Styles */
      @media (max-width: 768px) {
        /* Header */
        .site-header {
          padding: 1rem !important;
        }
        
        .site-header h1 {
          font-size: 1.5rem !important;
        }
        
        /* SHOW mobile buttons */
        .mobile-menu-btn,
        .mobile-search-btn {
          display: flex !important;
        }
        
        /* HIDE desktop elements */
        .desktop-search {
          display: none !important;
        }
        
        .desktop-only {
          display: none !important;
        }
        
        /* Navigation - HIDE completely on mobile */
        .site-nav {
          display: none !important;
        }
        
        /* Main Content */
        .site-main {
          padding: 1rem !important;
          max-width: 100% !important;
        }
        
        .site-main > div {
          padding: 0.5rem !important;
        }
        
        /* Welcome Banner - Compact on mobile */
        div[style*="linear-gradient(135deg, rgb(193, 121, 91)"],
        div[style*="linear-gradient(135deg, #c1795b"] {
          padding: 1rem 1.25rem !important;
          margin-bottom: 1rem !important;
          border-radius: 12px !important;
        }
        
        div[style*="linear-gradient(135deg, rgb(193, 121, 91)"] h2,
        div[style*="linear-gradient(135deg, #c1795b"] h2 {
          font-size: 1.3rem !important;
          margin-bottom: 0.25rem !important;
        }
        
        div[style*="linear-gradient(135deg, rgb(193, 121, 91)"] p,
        div[style*="linear-gradient(135deg, #c1795b"] p {
          font-size: 0.85rem !important;
          margin: 0 !important;
        }
        
        /* Designer - Fix yarn breakdown overflow */
        input[type="number"],
        input[type="text"] {
          max-width: 100% !important;
          width: 100% !important;
        }
        
        /* Designer form elements */
        form > div,
        form > div > div {
          max-width: 100% !important;
        }
        
        /* Yarn breakdown section specifically */
        div[style*="grid-template-columns"] {
          grid-template-columns: 1fr !important;
        }
        
        /* Make pattern grid single column on mobile */
        div[style*="display: grid"][style*="gridTemplateColumns"] {
          grid-template-columns: 1fr !important;
        }
        /* Pattern Grid - Make single column on mobile */
        main > div > div[style*="grid-template-columns"] {
          grid-template-columns: 1fr !important;
          gap: 1rem !important;
        }
        
        /* Pattern Card Images - Less tall */
        main > div > div > div > div > div[style*="paddingTop"] {
          padding-top: 75% !important;
        }
        
        /* Pattern Card Content - Tighter spacing */
        main > div > div > div > div:last-child {
          padding: 1rem !important;
        }
        
        /* Pattern Titles - Smaller */
        main h3 {
          font-size: 1.1rem !important;
        }
        
        /* Discover Patterns heading */
        main h2 {
          font-size: 1.5rem !important;
        }
      }
      
      @media (max-width: 480px) {
        .site-header {
          padding: 0.75rem 1rem !important;
        }
        
        .site-header h1 {
          font-size: 1.3rem !important;
        }
        
        .site-header button {
          padding: 0.5rem !important;
          font-size: 0.85rem !important;
        }
        
        /* Even more compact welcome banner on tiny screens */
        div[style*="linear-gradient(135deg, rgb(193, 121, 91)"] h2,
        div[style*="linear-gradient(135deg, #c1795b"] h2 {
          font-size: 1.1rem !important;
        }
        
        div[style*="linear-gradient(135deg, rgb(193, 121, 91)"] p,
        div[style*="linear-gradient(135deg, #c1795b"] p {
          font-size: 0.8rem !important;
        }
      }
    `}</style>
    <Analytics />
    <SpeedInsights />
    </div>
  );
}

export default TheSingleLoop;
